export type AutoNumberPosition = 'prefix' | 'suffix';

export type ContainerHeadingOptions = {
  position: AutoNumberPosition;
  separator: string;
};

/** Per-style overrides; omitted styles use defaults. */
export type ContainerHeadingStyleConfig = Partial<
  Record<string, ContainerHeadingOptions>
>;

export const DEFAULT_AUTO_NUMBER_POSITION: AutoNumberPosition = 'suffix';
export const DEFAULT_AUTO_NUMBER_SEPARATOR = ' ';

export const DEFAULT_CONTAINER_HEADING_CONFIG: ContainerHeadingStyleConfig = {};

const joinNonEmpty = (parts: string[], separator: string): string =>
  parts
    .map(part => part.trim())
    .filter(Boolean)
    .join(separator);

/**
 * When `prefix` or `suffix` is present (schema label / display suffix), the line
 * is `[prefix, autoNumber, title, suffix]` (title = heading text / `text`).
 * Otherwise legacy behaviour: combine `title` + `autoNumber` using position.
 */
export const formatContainerHeading = ({
  autoNumber,
  config = DEFAULT_CONTAINER_HEADING_CONFIG,
  defaultPosition = DEFAULT_AUTO_NUMBER_POSITION,
  defaultSeparator = DEFAULT_AUTO_NUMBER_SEPARATOR,
  numberingStylePosition,
  prefix,
  style,
  suffix,
  title,
}: {
  autoNumber: string;
  title: string;
  style: string;
  config?: ContainerHeadingStyleConfig;
  defaultPosition?: AutoNumberPosition;
  defaultSeparator?: string;
  numberingStylePosition?: AutoNumberPosition | null;
  prefix?: string;
  suffix?: string;
}): string => {
  const styleConfig = config[style];
  const position =
    numberingStylePosition ?? styleConfig?.position ?? defaultPosition;
  const separator = styleConfig?.separator ?? defaultSeparator;
  const numberToken = autoNumber.trim();
  const titleToken = title.trim();
  const prefixToken = (prefix ?? '').trim();
  const suffixToken = (suffix ?? '').trim();

  const useFlatComposition = prefixToken.length > 0 || suffixToken.length > 0;
  if (useFlatComposition) {
    const labelParts =
      position === 'suffix'
        ? [prefixToken, numberToken]
        : [numberToken, prefixToken];

    return joinNonEmpty([...labelParts, titleToken, suffixToken], separator);
  }

  if (numberToken.length === 0) {
    return titleToken;
  }
  if (titleToken.length === 0) {
    return numberToken;
  }
  return position === 'suffix'
    ? [titleToken, numberToken].join(separator)
    : [numberToken, titleToken].join(separator);
};
